/**
 * User-facing wording for CopyQuoteDialog's two failure surfaces (#22824).
 *
 * These are plain functions rather than inline JSX so the wording is unit-testable without jsdom,
 * and so each failure mode is named exactly once.
 */

interface GraphQLErrorLike {
  message?: string
  extensions?: { code?: string }
}

export interface QueryErrorLike {
  message?: string
  graphQLErrors?: ReadonlyArray<GraphQLErrorLike>
  networkError?: unknown
}

export interface GetQuoteLoadErrorArgs {
  /** The id the dialog was opened with. Callers can pass `quote?._id`, so it can be undefined. */
  quoteId?: string
  /** True while the query is in flight. Nothing is wrong yet. */
  loading?: boolean
  /** `data?.quote` from useQuoteQuery. */
  quote?: unknown
  error?: QueryErrorLike
  /**
   * True when the Apollo client believes it is offline. `@wora/apollo-offline` rewrites every
   * query to `cache-only` in that state, so the query never reaches the network and reports no
   * error — this is the production cause of #22824.
   */
  clientOffline?: boolean
  /** Already-localised object label, e.g. "Estimate" or "Invoice". */
  quoteLabel: string
}

function findGraphQLError(error: QueryErrorLike | undefined, predicate: (e: GraphQLErrorLike) => boolean) {
  return (error?.graphQLErrors ?? []).find(predicate)
}

/**
 * Returns null when the quote is usable, otherwise the reason the dialog cannot duplicate it.
 */
export function getQuoteLoadError(args: GetQuoteLoadErrorArgs): string | null {
  const { quoteId, loading, quote, error, clientOffline, quoteLabel } = args

  if (loading) {
    return null
  }

  if (!quoteId) {
    return `No ${quoteLabel} was selected to duplicate. Close this window and try again.`
  }

  if (findGraphQLError(error, (e) => e.extensions?.code === 'NOT_FOUND')) {
    return `This ${quoteLabel} no longer exists. It may have been deleted.`
  }

  if (findGraphQLError(error, (e) => e.extensions?.code === 'FORBIDDEN' || /not accessible/i.test(e.message ?? ''))) {
    return `You do not have access to this ${quoteLabel}.`
  }

  if (error?.networkError) {
    return 'We could not reach PaintScout. Check your connection, then try again.'
  }

  if (error) {
    const reason = error.graphQLErrors?.[0]?.message || error.message
    return reason ? `We could not load this ${quoteLabel}: ${reason}` : `We could not load this ${quoteLabel}.`
  }

  if (!quote) {
    if (clientOffline) {
      return `PaintScout is in offline mode, so it could not load this ${quoteLabel}. Reload the page, then try again.`
    }
    return `We could not load this ${quoteLabel}. Try again, or reload the page.`
  }

  return null
}

export interface GetQuoteCopyFailureMessageArgs {
  error?: unknown
  quoteLabel: string
}

/**
 * The Confirm-time snackbar. Keeps the plain prefix and appends the underlying reason, so the user
 * and support see more than "Unable to duplicate".
 */
export function getQuoteCopyFailureMessage(args: GetQuoteCopyFailureMessageArgs): string {
  const { error, quoteLabel } = args
  const base = `Unable to duplicate ${quoteLabel}`
  const reason = typeof (error as { message?: unknown })?.message === 'string' ? (error as Error).message.trim() : ''

  return reason ? `${base}: ${reason}` : base
}
